import React, { useEffect, useMemo, useState } from 'react';
import { Search, Euro, Calendar, User, Mail, FileText } from 'lucide-react';

const frDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (_) {
    return '';
  }
};

const sortFn = (a, b, key, dir) => {
  const va = a[key] ?? '';
  const vb = b[key] ?? '';
  let res = 0;
  if (key === 'date') {
    res = new Date(va) - new Date(vb);
  } else if (key === 'montant') {
    res = (parseFloat(va) || 0) - (parseFloat(vb) || 0);
  } else {
    res = ('' + va).localeCompare('' + vb, 'fr', { sensitivity: 'base', numeric: true });
  }
  return dir === 'asc' ? res : -res;
};

const FinancesManagement = () => {
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const pageSizeOptions = [50, 100, 200];
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ key: 'date', dir: 'desc' });

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = new URL('/api/admin/finances', API_BASE);
      const headers = { 'Content-Type': 'application/json' };
      const token = (typeof window !== 'undefined') ? localStorage.getItem('authToken') : null;
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const res = await fetch(url.toString(), { headers });
      if (!res.ok) throw new Error(`Requête échouée (${res.status})`);
      
      const data = await res.json();
      const items = (data?.finances || []).map((f) => ({
        id: f.id,
        date: f.date,
        type: f.type,
        nom: f.nom,
        email: f.email,
        description: f.description,
        montant: f.montant,
        statut: f.statut,
        methode_paiement: f.methode_paiement
      }));
      setRows(items);
      setPage(1);
    } catch (e) {
      setError(e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = rows;
    if (q) {
      list = rows.filter(r => (
        (r.nom || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q) ||
        (r.type || '').toLowerCase().includes(q) ||
        (r.description || '').toLowerCase().includes(q) ||
        (r.statut || '').toLowerCase().includes(q)
      ));
    }
    return list;
  }, [rows, search]);

  const sorted = useMemo(() => {
    if (!sort.key) return filtered;
    return [...filtered].sort((a, b) => sortFn(a, b, sort.key, sort.dir));
  }, [filtered, sort]);

  const totalPages = Math.ceil(sorted.length / pageSize);
  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  const handleSort = (key) => {
    setSort(prev => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  const totalMontant = useMemo(() => {
    return filtered.reduce((sum, r) => sum + (parseFloat(r.montant) || 0), 0);
  }, [filtered]);

  const getStatutBadge = (statut) => {
    // Tous les paiements affichés sont payés via Stripe
    return 'bg-green-100 text-green-800';
  };

  const getStatutLabel = (statut) => {
    // Tous les paiements affichés sont payés
    return 'Payée';
  };

  if (loading && rows.length === 0) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderBottomColor: 'rgb(196, 150, 96)' }}></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* En-tête */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: '"Lora", serif' }}>
          Historique des Paiements Stripe
        </h1>
        <p className="text-gray-600">
          Tous les paiements effectués via Stripe (cotisations et événements)
        </p>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4 border-l-4" style={{ borderLeftColor: 'rgb(196, 150, 96)' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Paiements Stripe</p>
              <p className="text-2xl font-bold" style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Lora", serif' }}>
                {filtered.length}
              </p>
            </div>
            <FileText className="h-8 w-8 text-gray-400" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Montant Total Encaissé</p>
              <p className="text-2xl font-bold text-green-600" style={{ fontFamily: '"Lora", serif' }}>
                {totalMontant.toFixed(2)} €
              </p>
            </div>
            <Euro className="h-8 w-8 text-gray-400" />
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          Erreur : {error}
        </div>
      )}

      {/* Barre de recherche et pagination */}
      <div className="bg-white rounded-lg shadow mb-4 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Recherche */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher (nom, email, type, description...)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
              style={{ focusRingColor: 'rgb(196, 150, 96)' }}
            />
          </div>

          {/* Sélecteur de taille de page */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Afficher :</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
              style={{ focusRingColor: 'rgb(196, 150, 96)' }}
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <span className="text-sm text-gray-600">résultats</span>
          </div>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  onClick={() => handleSort('date')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-1">
                    Date
                    {sort.key === 'date' && (
                      <span>{sort.dir === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('type')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-1">
                    Type
                    {sort.key === 'type' && (
                      <span>{sort.dir === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('nom')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-1">
                    Nom
                    {sort.key === 'nom' && (
                      <span>{sort.dir === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th
                  onClick={() => handleSort('montant')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-1">
                    Montant
                    {sort.key === 'montant' && (
                      <span>{sort.dir === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('statut')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-1">
                    Statut
                    {sort.key === 'statut' && (
                      <span>{sort.dir === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Méthode
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paged.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                    {search ? 'Aucun résultat trouvé pour votre recherche' : 'Aucun paiement Stripe enregistré'}
                  </td>
                </tr>
              ) : (
                paged.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {frDate(row.date)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="max-w-xs truncate" title={row.type}>
                        {row.type}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        {row.nom}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-gray-400" />
                        {row.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {row.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold" style={{ color: 'rgb(196, 150, 96)' }}>
                      {parseFloat(row.montant).toFixed(2)} €
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatutBadge(row.statut)}`}>
                        {getStatutLabel(row.statut)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {row.methode_paiement ? (
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          row.is_test 
                            ? 'bg-orange-100 text-orange-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {row.methode_paiement}
                        </span>
                      ) : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Page {page} sur {totalPages} • {filtered.length} paiement(s) Stripe
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Précédent
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinancesManagement;
